import AsyncStorage from '@react-native-async-storage/async-storage';
import { noop } from 'lodash';
export const TimestampStorageKeys = {
    RATING_ALERT_PROMPT: 'LAST_RATE_TIME',
    MARKETPLACE_REVIEW_PROMPT: 'LAST_REVIEW_PROMPT_TIME_MARKETPLACE',
    NOTIFICATION_PERMISSION_PROMPT: 'notification_modal_shown_at',
};
export class TimestampStorage {
    /**
     * Stores the current timestamp for the given key.
     * Does not throw (writing failure is ignored).
     */
    static async stamp(key) {
        await AsyncStorage.setItem(key, Date.now().toString()).catch(noop);
    }
    /**
     * Retrieves the stored timestamp as a Date object.
     * Handles both milliseconds and ISO string formats for backward compatibility.
     * Does not throw (resolves null if failed to get).
     *
     * @returns The stored Date, or null if not set or invalid.
     */
    static async get(key) {
        let value;
        try {
            value = await AsyncStorage.getItem(key);
        }
        catch {
            return null;
        }
        if (value === null)
            return null;
        const numberValue = parseInt(value, 10);
        if (numberValue.toString() === value) {
            return new Date(numberValue);
        }
        // try ISO string
        const date = new Date(value);
        if (!date.getTime()) {
            return null;
        }
        return date;
    }
    /**
     * Compares the stored timestamp against a duration ago.
     * Does not throw (resolves 'non-exist' if failed to get from storage).
     *
     * @param key
     * @param msAgo - Duration in milliseconds to compare against.
     * @returns 'before' if timestamp is older than msAgo (stamped more than msAgo ms in the past);
     *          'after' if timestamp is more recent than msAgo.
     * @see notSince quickly checks not-after
     */
    static async compare(key, msAgo) {
        const date = await this.get(key);
        if (date === null)
            return 'non-exist';
        return this.dateCompare(date, msAgo);
    }
    /**
     * Does not throw (resolves true if failed to get from storage).
     * @returns true if never stamped OR stamped more than msAgo ms ago.
     */
    static async notSince(key, msAgo) {
        return (await this.compare(key, msAgo)) !== 'after';
    }
    /**
     * Compares a Date against a duration ago.
     * @returns 'before' if date is older than msAgo; 'after' if more recent.
     */
    static dateCompare(date, msAgo) {
        if (Date.now() - msAgo > date.getTime()) {
            return 'before';
        }
        return 'after';
    }
    /**
     * `dateIsNotAfter(date, msAgo: n)` reads "date is not after n ms ago"
     * @param date
     * @param msAgo
     * @returns false if date is nonnull and is after `msAgo` ms ago
     */
    static dateIsNotAfter(date, msAgo) {
        if (date === null)
            return true;
        return this.dateCompare(date, msAgo) !== 'after';
    }
}
